import type {
  DigitalInformationType,
  MessageErrorType,
  MessageImageType,
  MessageItemType,
  MessageLinksType,
  MessageListType,
  MessageRelatedQuestionsType,
  SocialMediaAccountsType,
} from '../types/message';

type RequestStatus = 'idle' | 'loading' | 'success' | 'error';

export interface ProfileState {
  prompt: string | null;
  response: {
    content: string | null;
    links: MessageLinksType[];
    images: MessageImageType[];
    relatedQuestions: MessageRelatedQuestionsType[];
    socialMediaAccounts?: SocialMediaAccountsType;
    digitalInformation?: DigitalInformationType;
  } | null;
  status: RequestStatus;
  error: MessageErrorType | null;
}

export interface FollowUpState {
  id: string;
  status: RequestStatus;
  error: MessageErrorType | null;
  messages: MessageItemType[];
}

interface SplitConversationMessagesResult {
  profileUserMessage?: MessageItemType;
  profileAssistantMessage?: MessageItemType;
  followUpMessages: MessageItemType[];
}

function splitConversationMessages(
  messages: MessageListType,
): SplitConversationMessagesResult {
  const profileUserIndex = messages.findIndex(
    (message) => message.role === 'user',
  );
  const profileUserMessage =
    profileUserIndex >= 0 ? messages[profileUserIndex] : undefined;

  const profileAssistantIndex = messages.findIndex(
    (message, index) =>
      index > profileUserIndex && message.role === 'assistant',
  );
  const profileAssistantMessage =
    profileAssistantIndex >= 0 ? messages[profileAssistantIndex] : undefined;

  const followUpMessages =
    profileAssistantIndex >= 0 ? messages.slice(profileAssistantIndex + 1) : [];

  return {
    profileUserMessage,
    profileAssistantMessage,
    followUpMessages,
  };
}

function resolveStatus({
  hasError,
  hasResponse,
  hasRequest,
}: {
  hasError: boolean;
  hasResponse: boolean;
  hasRequest: boolean;
}): RequestStatus {
  if (hasError) {
    return 'error';
  }

  if (hasResponse) {
    return 'success';
  }

  if (hasRequest) {
    return 'loading';
  }

  return 'idle';
}

function findLatestAssistantMessage(
  messages: MessageItemType[],
): MessageItemType | undefined {
  return [...messages]
    .reverse()
    .find((message) => message.role === 'assistant');
}

function deriveProfileState({
  profileUserMessage,
  profileAssistantMessage,
}: {
  profileUserMessage?: MessageItemType;
  profileAssistantMessage?: MessageItemType;
}): ProfileState | null {
  if (
    profileUserMessage === undefined &&
    profileAssistantMessage === undefined
  ) {
    return null;
  }

  return {
    prompt: profileUserMessage?.content ?? null,
    response:
      profileAssistantMessage?.error === undefined &&
      profileAssistantMessage !== undefined
        ? {
            content: profileAssistantMessage.content ?? null,
            links: profileAssistantMessage.links ?? [],
            images: profileAssistantMessage.images ?? [],
            relatedQuestions: profileAssistantMessage.relatedQuestions ?? [],
            socialMediaAccounts: profileAssistantMessage.socialMediaAccounts,
            digitalInformation: profileAssistantMessage.digitalInformation,
          }
        : null,
    status: resolveStatus({
      hasError: profileAssistantMessage?.error !== undefined,
      hasResponse: profileAssistantMessage !== undefined,
      hasRequest: profileUserMessage !== undefined,
    }),
    error: profileAssistantMessage?.error ?? null,
  };
}

function deriveFollowUpState(
  followUpMessages: MessageItemType[],
): FollowUpState | null {
  if (followUpMessages.length === 0) {
    return null;
  }

  const latestFollowUpAssistantMessage =
    findLatestAssistantMessage(followUpMessages);

  return {
    id: followUpMessages[0].id,
    status: resolveStatus({
      hasError: latestFollowUpAssistantMessage?.error !== undefined,
      hasResponse: latestFollowUpAssistantMessage !== undefined,
      hasRequest: followUpMessages.length > 0,
    }),
    error: latestFollowUpAssistantMessage?.error ?? null,
    messages: followUpMessages,
  };
}

export function deriveNormalizedConversationMessages(
  messages: MessageListType,
): {
  profileMessage?: MessageItemType;
  followUpMessages: MessageItemType[];
} {
  const { profileAssistantMessage, followUpMessages } =
    splitConversationMessages(messages);

  return {
    profileMessage: profileAssistantMessage,
    followUpMessages,
  };
}

export function deriveCurrentError(
  profile: ProfileState | null,
  followUp: FollowUpState | null,
): MessageErrorType | null {
  if (followUp?.error != null) {
    return followUp.error;
  }

  return profile?.error ?? null;
}

export function deriveConversationState(messages: MessageListType): {
  profile: ProfileState | null;
  followUp: FollowUpState | null;
} {
  const { profileUserMessage, profileAssistantMessage, followUpMessages } =
    splitConversationMessages(messages);

  const profile = deriveProfileState({
    profileUserMessage,
    profileAssistantMessage,
  });
  const followUp = deriveFollowUpState(followUpMessages);

  return {
    profile,
    followUp,
  };
}
